<template>
  <div class="rounded-md border focus-within:ring-1 focus-within:ring-ring">
    <div v-if="editor" class="flex flex-wrap items-center gap-0.5 border-b bg-muted/40 px-1.5 py-1">
      <button v-for="tool in tools" :key="tool.name" type="button" :title="tool.title"
              class="rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
              :class="{ 'bg-accent text-foreground': tool.active && tool.active() }"
              @mousedown.prevent="tool.run">
        <span v-html="tool.label"></span>
      </button>
    </div>
    <div class="markdown-editor cursor-text px-3 py-2 text-sm" :style="{ minHeight: minHeight }" @click="focusEditor">
      <editor-content :editor="editor" />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import Link from "@tiptap/extension-link";

export default {
  name: "MarkdownEditor",
  components: { EditorContent },
  props: {
    modelValue: { type: String, default: "" },
    rows: { type: [Number, String], default: 5 },
    placeholder: { type: String, default: "" },
  },
  emits: ["update:modelValue", "blur", "focus"],
  data() {
    return { editor: null };
  },
  computed: {
    minHeight() {
      return `${Number(this.rows) * 1.5 + 1}rem`;
    },
    tools() {
      const e = this.editor;
      return [
        { name: "bold", title: "Gras", label: "<b>B</b>", run: () => e.chain().focus().toggleBold().run(), active: () => e.isActive("bold") },
        { name: "italic", title: "Italique", label: "<i>I</i>", run: () => e.chain().focus().toggleItalic().run(), active: () => e.isActive("italic") },
        { name: "strike", title: "Barré", label: "<s>S</s>", run: () => e.chain().focus().toggleStrike().run(), active: () => e.isActive("strike") },
        { name: "code", title: "Code", label: "<code>&lt;/&gt;</code>", run: () => e.chain().focus().toggleCode().run(), active: () => e.isActive("code") },
        { name: "h3", title: "Titre", label: "H", run: () => e.chain().focus().toggleHeading({ level: 3 }).run(), active: () => e.isActive("heading", { level: 3 }) },
        { name: "ul", title: "Liste", label: "•", run: () => e.chain().focus().toggleBulletList().run(), active: () => e.isActive("bulletList") },
        { name: "ol", title: "Liste numérotée", label: "1.", run: () => e.chain().focus().toggleOrderedList().run(), active: () => e.isActive("orderedList") },
        { name: "quote", title: "Citation", label: "&ldquo;", run: () => e.chain().focus().toggleBlockquote().run(), active: () => e.isActive("blockquote") },
        { name: "link", title: "Lien", label: "🔗", run: () => this.setLink() },
      ];
    },
  },
  watch: {
    modelValue(value) {
      if (!this.editor) return;
      const current = this.editor.storage.markdown.getMarkdown();
      if (value !== current) {
        this.editor.commands.setContent(value || "", false);
      }
    },
  },
  mounted() {
    this.editor = new Editor({
      extensions: [
        StarterKit,
        Markdown.configure({ html: false, linkify: true, breaks: true }),
        Link.configure({ openOnClick: false }),
      ],
      content: this.modelValue || "",
      editorProps: {
        attributes: { class: "prose prose-sm max-w-none focus:outline-none" },
      },
      onUpdate: () => {
        this.$emit("update:modelValue", this.editor.storage.markdown.getMarkdown());
      },
      onBlur: () => this.$emit("blur"),
      onFocus: () => this.$emit("focus"),
    });
  },
  beforeUnmount() {
    if (this.editor) this.editor.destroy();
  },
  methods: {
    focusEditor(event) {
      if (this.editor && !this.editor.isFocused && !event.target.closest("a")) {
        this.editor.commands.focus("end");
      }
    },
    setLink() {
      const previous = this.editor.getAttributes("link").href;
      const url = window.prompt("URL du lien", previous || "https://");
      if (url === null) return;
      if (url === "") {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }
      this.editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    },
  },
};
</script>

<style scoped>
.markdown-editor :deep(.ProseMirror) {
  outline: none;
  min-height: inherit;
}
.markdown-editor :deep(.ProseMirror p) {
  margin: 0 0 0.5em;
}
.markdown-editor :deep(.ProseMirror > *:last-child) {
  margin-bottom: 0;
}
</style>
